import React, { useEffect, useState } from "react";
import { Shield, ShieldCheck, AlertTriangle, Copy, Download } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { toast } from "sonner";
import api from "../../lib/apiClient";
import PhoneChangeCard from "./PhoneChangeCard";
import CommsSnoozeCard from "./CommsSnoozeCard";
import BuildInfoCard from "./BuildInfoCard";

// SecurityTab — manages TOTP 2FA enrollment + disable + backup codes.
// Single source of truth for the user's 2FA state lives on the server
// (`GET /auth/2fa/status`). We refresh it on tab mount and after any
// mutation.
const SecurityTab = ({ user, refreshUser }) => {
  const [status, setStatus] = useState({ enabled: false, backup_codes_remaining: 0, required_for_role: false });
  const [loading, setLoading] = useState(true);

  // Enrollment state
  const [enrollData, setEnrollData] = useState(null); // {secret, otpauth_uri, qr_data_uri}
  const [enrollCode, setEnrollCode] = useState("");
  const [enrolling, setEnrolling] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null); // shown once

  // Disable state
  const [disablePw, setDisablePw] = useState("");
  const [disabling, setDisabling] = useState(false);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/auth/2fa/status");
      setStatus(data);
    } catch (_) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchStatus(); }, []);

  const startEnroll = async () => {
    try {
      const { data } = await api.post("/auth/2fa/enroll-start");
      setEnrollData(data);
      setEnrollCode("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start enrollment.");
    }
  };

  const confirmEnroll = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(enrollCode)) {
      toast.error("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setEnrolling(true);
    try {
      const { data } = await api.post("/auth/2fa/enroll-confirm", { code: enrollCode });
      setBackupCodes(data.backup_codes);
      setEnrollData(null);
      setEnrollCode("");
      toast.success("Two-factor authentication enabled.");
      await fetchStatus();
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That code is incorrect. Try again.");
    }
    setEnrolling(false);
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    if (!disablePw) return;
    setDisabling(true);
    try {
      await api.post("/auth/2fa/disable", { password: disablePw });
      setDisablePw("");
      toast.success("2FA disabled.");
      await fetchStatus();
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't disable 2FA.");
    }
    setDisabling(false);
  };

  const copyCodes = async () => {
    if (!backupCodes) return;
    try {
      await navigator.clipboard.writeText(backupCodes.join("\n"));
      toast.success("Backup codes copied to clipboard.");
    } catch (_) {
      toast.error("Copy failed. Save the codes manually.");
    }
  };

  const downloadCodes = () => {
    if (!backupCodes) return;
    const blob = new Blob(
      [`GlobalEyes.TV 2FA Backup Codes\nAccount: ${user.username}\nGenerated: ${new Date().toISOString()}\n\n${backupCodes.join("\n")}\n\nEach code can be used ONCE if you lose access to your authenticator app.`],
      { type: "text/plain" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `globaleyes-2fa-backup-codes-${user.username}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Render the 2FA card based on current state. Extracted so we can
  // always render the phone-change + snooze cards alongside it.
  const renderTwoFA = () => {
    if (loading) {
      return <Card className="bg-gray-900 border-gray-800"><CardContent className="p-6 text-gray-400 text-sm">Loading…</CardContent></Card>;
    }
    if (backupCodes) { return renderBackupCodes(); }
    if (status.enabled) { return renderEnrolled(); }
    if (enrollData) { return renderEnrollment(); }
    return renderNotEnrolled();
  };

  const renderBackupCodes = () => (
      <Card className="bg-gray-900 border-amber-500/40" data-testid="twofa-backup-codes-card">
        <CardHeader>
          <CardTitle className="text-amber-300 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Save your backup codes NOW</CardTitle>
          <CardDescription className="text-gray-400">
            These 10 codes are your only way back in if you lose access to your authenticator app.
            <span className="block mt-1 text-amber-300 font-medium">You will not see these codes again.</span> Each can be used <strong>once</strong>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 font-mono text-sm bg-gray-950 rounded p-3 border border-gray-800">
            {backupCodes.map((c) => (
              <div key={c} className="text-emerald-300 text-center" data-testid="twofa-backup-code">{c}</div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={copyCodes} className="bg-gray-800 hover:bg-gray-700" data-testid="twofa-copy-codes-btn"><Copy className="h-4 w-4 mr-2" /> Copy to clipboard</Button>
            <Button onClick={downloadCodes} className="bg-gray-800 hover:bg-gray-700" data-testid="twofa-download-codes-btn"><Download className="h-4 w-4 mr-2" /> Download as .txt</Button>
            <Button onClick={() => setBackupCodes(null)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="twofa-codes-saved-btn">I've saved them — done</Button>
          </div>
        </CardContent>
      </Card>
  );

  const renderEnrolled = () => (
      <Card className="bg-gray-900 border-emerald-500/30" data-testid="twofa-enrolled-card">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-400" /> Two-factor authentication is on
          </CardTitle>
          <CardDescription className="text-gray-400">
            You'll be asked for a 6-digit code from your authenticator app when changing your password{user.is_admin ? " or running any admin action" : ""}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-gray-300">
            <p>Backup codes remaining: <span className="font-semibold text-emerald-300" data-testid="twofa-backup-remaining">{status.backup_codes_remaining}</span> / 10</p>
            {status.backup_codes_remaining <= 3 && (
              <p className="text-amber-300 text-xs mt-1">Low on codes — disable + re-enable 2FA to regenerate (you'll need your authenticator app handy).</p>
            )}
          </div>
          {user.is_admin ? (
            <p className="text-xs text-amber-300/80 bg-amber-500/5 border border-amber-500/20 rounded p-3" data-testid="twofa-admin-locked-note">
              Admin accounts cannot disable 2FA. If you've lost your device, use a backup code to log in then contact platform support.
            </p>
          ) : (
            <form onSubmit={handleDisable} className="space-y-2 max-w-sm">
              <Label className="text-gray-300 text-sm">Disable 2FA — enter your password to confirm</Label>
              <Input
                type="password"
                value={disablePw}
                onChange={(e) => setDisablePw(e.target.value)}
                placeholder="••••••••"
                className="bg-gray-800 border-gray-700 text-white"
                data-testid="twofa-disable-password"
              />
              <Button type="submit" disabled={disabling || !disablePw} className="bg-red-600 hover:bg-red-700" data-testid="twofa-disable-btn">
                {disabling ? "Disabling..." : "Disable 2FA"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
  );

  const renderEnrollment = () => (
      <Card className="bg-gray-900 border-gray-800" data-testid="twofa-enroll-card">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2"><Shield className="h-5 w-5 text-emerald-400" /> Set up two-factor authentication</CardTitle>
          <CardDescription className="text-gray-400">
            Scan this QR code with Google Authenticator, 1Password, Authy, or Apple Passwords — then enter the 6-digit code it shows.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center sm:items-start">
            <img src={enrollData.qr_data_uri} alt="2FA QR code" className="bg-white p-2 rounded w-48 h-48" data-testid="twofa-enroll-qr" />
            <div className="text-sm text-gray-300 space-y-2 flex-1">
              <p className="text-gray-400">Can't scan? Manually enter this secret:</p>
              <code className="block bg-gray-950 border border-gray-800 rounded p-2 text-emerald-300 font-mono text-xs break-all" data-testid="twofa-enroll-secret">{enrollData.secret}</code>
              <p className="text-xs text-gray-500">Issuer: GlobalEyes.TV — Account: {user.email || user.username}</p>
            </div>
          </div>
          <form onSubmit={confirmEnroll} className="space-y-2 max-w-sm">
            <Label className="text-gray-300 text-sm">Enter the 6-digit code shown by your authenticator</Label>
            <Input
              type="text"
              value={enrollCode}
              onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="123456"
              inputMode="numeric"
              autoComplete="one-time-code"
              className="bg-gray-800 border-gray-700 text-white text-center tracking-[0.4em] text-lg font-mono"
              data-testid="twofa-enroll-code"
            />
            <div className="flex gap-2">
              <Button type="submit" disabled={enrolling || enrollCode.length !== 6} className="bg-emerald-600 hover:bg-emerald-700" data-testid="twofa-enroll-confirm-btn">
                {enrolling ? "Verifying..." : "Verify & enable"}
              </Button>
              <Button type="button" onClick={() => { setEnrollData(null); setEnrollCode(""); }} className="bg-gray-800 hover:bg-gray-700" data-testid="twofa-enroll-cancel-btn">Cancel</Button>
            </div>
          </form>
        </CardContent>
      </Card>
  );

  const renderNotEnrolled = () => (
    <Card className={`bg-gray-900 ${status.required_for_role ? "border-amber-500/40" : "border-gray-800"}`} data-testid="twofa-not-enrolled-card">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2"><Shield className="h-5 w-5 text-emerald-400" /> Two-factor authentication</CardTitle>
        <CardDescription className="text-gray-400">
          {status.required_for_role
            ? <span className="text-amber-300"><strong>Required for admin accounts.</strong> You won't be able to run any admin actions until 2FA is enabled.</span>
            : <>Add a second step to your login when changing your password or running sensitive actions. Free, works offline, takes 60 seconds.</>}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button onClick={startEnroll} className="bg-emerald-600 hover:bg-emerald-700" data-testid="twofa-enable-btn">
          <Shield className="h-4 w-4 mr-2" /> Enable two-factor authentication
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4" data-testid="security-tab">
      {renderTwoFA()}
      <PhoneChangeCard user={user} refreshUser={refreshUser} />
      <CommsSnoozeCard />
      <BuildInfoCard />
    </div>
  );
};

export default SecurityTab;
